<template>
  <PForm
    ref="pForm"
    :heading="`${documentFormState.edit ? 'Edit ' : 'Upload '} Document`"
    :primary-cta="submitBtn"
    :secondary-cta="{}"
    @close="closeForm(true)"
    @submit.prevent="onSubmit"
  >
    <div class="row">
      <div class="col-12">
        <PSelectInput
          v-model="formData.sub_category"
          class="text-capitalize"
          :disable="documentFormState.edit"
          input-debounce="300"
          option-label="name"
          option-value="name"
          :options="unref(categoryFilter.entities)"
          :options-loading="unref(categoryFilter.loading)"
          label="Document Category"
          :validations="{ required: true }"
          placeholder="Select document category"
          @filter="categoryFilter.onFilter"
          @filter-abort="categoryFilter.onFilterAbort"
        />
      </div>
    </div>

    <div class="row q-mt-lg">
      <div class="col-12">
        <PSelectInput
          v-model="computedDocumentId"
          option-label="name"
          option-value="_id"
          :options="unref(subCategoryFilter.entities)"
          :loading="subCategoryLoading"
          label="Document Name"
          :disable="documentFormState.edit || !formData.sub_category.length"
          :validations="{ required: true }"
          placeholder="Select document name"
          @filter="subCategoryFilter.onFilter"
          @filter-abort="subCategoryFilter.onFilterAbort"
        />
      </div>
    </div>

    <div
      v-if="
        formData?.document?.post_dated_cheque || formData?.post_dated_cheque
      "
      class="row q-col-gutter-x-lg q-mt-lg"
    >
      <div class="col-6">
        <PSelectInput
          v-model="formData.number_of_cheques"
          :options="
            Array.from(
              { length: formData.max_documents },
              (_, index) => index + 1,
            )
          "
          label="Number of Cheque"
          :disable="documentFormState.edit"
          :validations="{ required: true }"
          placeholder="Select number of cheque"
        />
      </div>
      <div class="col-6">
        <PInput
          v-model.trim.number="formData.amount_per_cheque"
          label="Amount per Cheque"
          :validations="{ required: true }"
          placeholder="0"
          :disable="documentFormState.edit"
          class="prepend-bg"
          type="number"
        >
          <template #prepend>
            <span class="text-sm">₹</span>
          </template></PInput
        >
      </div>
    </div>

    <div
      v-if="formData.default_template || formData.document?.default_template"
      class="row q-mt-lg"
    >
      <PCheckbox
        v-model="isSignedDocument"
        label="This is a signed document"
        right-label
      />
    </div>

    <div class="row q-mt-lg">
      <div class="col-12">
        <PFile
          v-model="formData.files"
          :required="isFileUploadRequired"
          multiple
          append
          accept=".pdf, .jpeg, .jpg, .png, .xlsx, .csv"
          :max-files="
            documentFormState.edit
              ? formData.max_documents
              : formData.max_documents - (formData.document?.files?.length ?? 0)
          "
          :disable="isFileUploadDisabled"
          variant="upload"
          label="Attach Document"
          :help="
            'Allowed file type are pdf, images, excel, csv. Max size of each files is 5MB, Max Documents allowed (' +
            (formData.max_documents ?? '-') +
            ')' +
            `${
              formData.document?.files?.length
                ? `\nNote: You already have ${formData.document?.files?.length} file(s) uploaded for this document`
                : ''
            }`
          "
        />
        <q-list v-if="formData.files?.length">
          <FileListItem
            v-for="(file, index) in formData.files"
            :key="index"
            :file="file"
            @view="handleViewFile(file)"
            @remove="removeFile(file, index)"
          />
        </q-list>
        <div v-if="isSubmitDisabled" class="q-mt-sm text-negative text-sm">
          Note: No template associated. Either associate a template or attach a
          signed file to continue.
        </div>
      </div>
    </div>
  </PForm>
</template>
<script setup>
import documentFormModel from "src/modules/user/models/documentFormModel";
import * as documentMasterApi from "src/api/document-master";

import { unref, watch } from "vue";

const pForm = ref(null);

const { documentFormState, user } = storeToRefs(useUserStore());
const { toggleDocumentForm, getClonedUserDocument, getUserDocuments } =
  useUserStore();

const { uploadOnboardingFile, viewFile } = useFile();
const { removeOnboardingFile } = useFileStore();

const props = defineProps({
  document: {
    type: Object,
    default: () => {},
  },
  isFolderView: {
    type: Boolean,
    default: false,
  },
});

const document = computed(() => props.document);

const { formData, closeForm } = useForm({
  formModel: documentFormModel(),
  entity: document,
  formRef: pForm,
  isForm: documentFormState,
  toggleForm: toggleDocumentForm,
});

const isSignedDocument = ref(false);

const skipFileTypes = [
  "post_dated_cheque",
  "bank_statement",
  "general_ledger",
  "vendor_agreement",
  "customer_agreement",
  "tds",
];

const isFileUploadRequired = computed({
  get() {
    if (unref(documentFormState).edit) {
      if (skipFileTypes.includes(unref(formData).code)) {
        return Boolean(unref(formData).user_id);
      }
    } else if (skipFileTypes.includes(unref(formData).document?.code)) {
      return Boolean(unref(formData).user_id);
    }

    return true;
  },
});

const isFileUploadDisabled = computed({
  get() {
    const formDataValue = unref(formData);

    return (
      ((formDataValue?.document?.default_template ||
        formDataValue?.default_template) &&
        !isSignedDocument.value) ||
      formDataValue.files?.length >= formDataValue.max_documents ||
      formDataValue.document?.files?.length >= formDataValue.max_documents
    );
  },
});

const handleViewFile = (file) => {
  if (file._id) {
    viewFile(file);
  } else {
    let url = URL.createObjectURL(file);

    window.open(url, "_blank");
  }
};

const removeFile = async (file, index) => {
  try {
    if (!unref(documentFormState).edit && !file._id) {
      // new file
      unref(formData).files.splice(index, 1);
    } else if (unref(documentFormState).edit && !file._id) {
      // new file
      unref(formData).files.splice(index, 1);
    } else {
      await removeOnboardingFile(file._id, { _id: unref(formData)._id });

      unref(formData).files.splice(index, 1);
    }
  } catch (error) {
    console.log(error);
  }
};

const categoryFilter = useFilter({
  entity: "documentMaster",
  options: {
    params: {
      category: "onboarding",
    },
  },
});

let subCategoryFilter = ref({});
let subCategoryLoading = ref(false);

watchEffect(async () => {
  try {
    subCategoryLoading.value = true;

    let response = await documentMasterApi.get({
      params: {
        category: "onboarding",
        sub_category: unref(formData).sub_category.toLowerCase(),
        user_id: unref(user)._id,
        applies_to: [
          unref(user)?.businessDetails?.business_type?.code,
          unref(user)?.basicInfo?.role,
        ],
      },
    });

    let documents = response?.data?.data;

    if (documents.length) {
      subCategoryFilter.value = useFilter({
        entities: documents,
        entity: "documentMaster",
      });
    } else {
      subCategoryFilter.value = {};
    }

    subCategoryLoading.value = false;
  } catch (error) {
    console.error(error);

    subCategoryLoading.value = false;
  }
});

const computedDocumentId = computed({
  get() {
    return unref(formData)._id;
  },
  set(documentId) {
    const documents = unref(unref(subCategoryFilter).entities);
    let selectedDocument = null;

    if (documents != null) {
      selectedDocument = documents.find(
        (document) => document._id == documentId,
      );
    }

    if (unref(formData)._id != documentId) {
      unref(formData).files = [];

      unref(formData).amount_per_cheque = null;

      unref(formData).number_of_cheques = null;
    }

    unref(formData)._id = documentId;

    unref(formData).max_documents = selectedDocument?.max_documents;

    unref(formData).user_id = selectedDocument?.user_id;

    unref(formData).document = selectedDocument;

    unref(formData).amount_per_cheque = selectedDocument?.amount_per_cheque;

    unref(formData).number_of_cheques = selectedDocument?.number_of_cheques;

    unref(pForm)?.component?.resetValidation();
  },
});

const computedSubCategory = computed(() => unref(formData).sub_category);

watch(computedSubCategory, (newSubCategory, previousSubCategory) => {
  if (
    newSubCategory &&
    previousSubCategory &&
    newSubCategory !== previousSubCategory
  ) {
    computedDocumentId.value = "";
  }
});

const isFileActionLoading = ref(false);
const isLoading = ref(false);

const isSubmitDisabled = computed({
  get() {
    if (!unref(documentFormState).edit) {
      const document = unref(formData)?.document;

      if (document?.default_template) {
        // special document
        if (!document?.contract_template_id && !unref(formData).files?.length) {
          // neither template present nor file attached
          return true;
        }
      }
    }

    return false;
  },
});

const submitBtn = computed(() => {
  return {
    loading: unref(isLoading),
    disable: unref(isSubmitDisabled),
  };
});

const attachDocuments = async (document) => {
  try {
    isFileActionLoading.value = true;

    let files = unref(formData).files;

    if (Array.isArray(files)) {
      let filesToUpload = [];

      for (const file of files) {
        if (file._id) {
          return;
        }

        filesToUpload.push(file);
      }

      if (filesToUpload.length) {
        await uploadOnboardingFile({
          shop_name: unref(user).businessDetails.shop_name,
          type: document.code,
          category: document.category,
          file: filesToUpload,
          user_id: document.user_id,
          user_document_id: document._id,
          label: document.default_template ? unref(isSignedDocument) : null,
        });
      }
    } else {
      await uploadOnboardingFile({
        shop_name: unref(user).businessDetails.shop_name,
        type: document.code,
        category: document.category,
        file: [files],
        user_id: document.user_id,
        user_document_id: document._id,
        label: document.default_template ? unref(isSignedDocument) : null,
      });
    }

    isFileActionLoading.value = false;
  } catch (error) {
    console.error(error);

    isFileActionLoading.value = false;
  }
};

const onSubmit = async () => {
  try {
    isLoading.value = true;

    if (!unref(documentFormState).edit) {
      if (!unref(formData).user_id) {
        // document not cloned yet
        let selectedFiles = unref(formData).files;
        let response = await getClonedUserDocument(
          {
            file: Array.isArray(selectedFiles)
              ? selectedFiles
              : [selectedFiles],
            shop_name: unref(user).businessDetails.shop_name,
            file_type: unref(formData).document.code,
            file_category: unref(formData).document.category,
          },
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            params: {
              PII: 1,
              user_id: unref(user)._id,
              document_id: unref(formData)._id,
              number_of_cheques: unref(formData).number_of_cheques,
              amount_per_cheque: unref(formData).amount_per_cheque,
              label: unref(formData).document.default_template
                ? unref(isSignedDocument)
                : null,
            },
          },
        );

        console.log(response.data?.data);
      } else {
        await attachDocuments(unref(formData).document);
      }
    } else {
      await attachDocuments(unref(formData));
    }

    if (unref(props).isFolderView) {
      await getUserDocuments({ groupBySubCategories: 1 });
    } else {
      await getUserDocuments();
    }

    closeForm(true);

    isSignedDocument.value = false;

    isLoading.value = false;
  } catch (error) {
    console.log(error);

    isLoading.value = false;
  }
};
</script>
